import {
    IonAlert,
    IonButton,
    IonCard,
    IonCardContent,
    IonIcon,
    useIonRouter,
} from "@ionic/react";
import { IntegrationPartner, MatchPhone } from "refsix-js-models";
import { FC, useMemo } from "react";
import { lockClosed, lockOpen } from "ionicons/icons";
import { IntegrationIcon } from "./IntegrationIcons";
import "./IntegrationMatchEditConnection.css";
import { useFormContext } from "react-hook-form";
import { updateMatch } from "../../services/matchService";
import { handleRefreshIntegrationMatches } from "../../services/integrations/integrationSyncUpdates";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import { integrationPartnerName } from "refsix-core";
import {
    trackDisconnectedIntegrationMatch,
    trackReconnectedIntegrationMatch,
} from "../../services/analytics/analyticsService";

interface IntegrationMatchEditConnectionProps {
    match: MatchPhone;
}
export const IntegrationMatchConnection: FC<
    IntegrationMatchEditConnectionProps
> = ({ match }) => {
    const { watch } = useFormContext();
    const integration = watch("integration");
    const allowIntegrationDisconnect = useMemo(() => {
        const allowIntegrationDisconnectPartners = [
            IntegrationPartner.assignr,
            IntegrationPartner.refinsight,
            IntegrationPartner.refassist,
        ];
        if (integration) {
            return allowIntegrationDisconnectPartners.includes(
                integration.partner
            );
        }
        return false;
    }, [integration]);

    if (allowIntegrationDisconnect && integration) {
        if (integration.disconnected) {
            return <ReconnectIntegration match={match} />;
        } else {
            return <DisconnectIntegration match={match} />;
        }
    }

    return null;
};

const DisconnectIntegration: FC<IntegrationMatchEditConnectionProps> = ({
    match,
}) => {
    const { setValue, watch } = useFormContext();
    const integration = watch("integration");
    const { t } = useTranslation();
    const tOptions = { partner: integrationPartnerName(integration?.partner) };

    return (
        <IonCard className="integration-card">
            <IonCardContent className="integration-card-content">
                <div className="integration-card-header">
                    <div className="integration-card-header-logo">
                        <IntegrationIcon partner={integration?.partner} />{" "}
                        <span style={{ marginLeft: 5 }}>
                            {t(
                                "integrations.syncing.integrationMatch",
                                tOptions
                            )}
                        </span>
                    </div>
                    <IonButton
                        fill="clear"
                        color="warning"
                        size={"small"}
                        className="integration-card-header-button"
                        id={"integration-disconnect-button"}
                    >
                        {t("integrations.syncing.disconnect")}{" "}
                        <IonIcon src={lockOpen} />
                    </IonButton>
                    <IonAlert
                        trigger={"integration-disconnect-button"}
                        header={t("integrations.syncing.disconnect")}
                        message={t(
                            "integrations.syncing.disconnectingConfirmation",
                            tOptions
                        )}
                        buttons={[
                            {
                                text: t("general.cancel"),
                                role: "cancel",
                                cssClass: "secondary",
                            },
                            {
                                text: t("general.yes"),
                                handler: () => {
                                    setValue("integration.disconnected", true);
                                    trackDisconnectedIntegrationMatch(match);
                                },
                            },
                        ]}
                    />
                </div>
                {t("integrations.syncing.disconnecting", tOptions)}
            </IonCardContent>
        </IonCard>
    );
};
const ReconnectIntegration: FC<IntegrationMatchEditConnectionProps> = ({
    match,
}) => {
    const { watch } = useFormContext();
    const integration = watch("integration");
    const route = useIonRouter();
    const { t } = useTranslation();
    const tOptions = { partner: capitalize(integration?.partner) };

    return (
        <IonCard className="integration-card">
            <IonCardContent className="integration-card-content">
                <div className="integration-card-header">
                    <div className="integration-card-header-logo">
                        <IntegrationIcon partner={integration?.partner} />
                        <span style={{ marginLeft: 5 }}>
                            {t(
                                "integrations.syncing.integrationMatch",
                                tOptions
                            )}
                        </span>
                    </div>
                    <IonButton
                        fill="clear"
                        color="primary"
                        size={"small"}
                        className="integration-card-header-button"
                        id={"integration-disconnect-button"}
                    >
                        {t("integrations.syncing.reconnect")}{" "}
                        <IonIcon src={lockClosed} />
                    </IonButton>
                    <IonAlert
                        trigger={"integration-disconnect-button"}
                        header={t("integrations.syncing.reconnect")}
                        message={t(
                            "integrations.syncing.reconnectingConfirmation",
                            tOptions
                        )}
                        buttons={[
                            {
                                text: t("general.cancel"),
                                role: "cancel",
                                cssClass: "secondary",
                            },
                            {
                                text: t("general.yes"),
                                handler: async () => {
                                    // Reconnecting the integration
                                    // Save the integration.disconnected state and refresh the match
                                    if (!match.integration) {
                                        return;
                                    }
                                    match.integration.disconnected = false;
                                    await updateMatch(match);
                                    await handleRefreshIntegrationMatches();
                                    trackReconnectedIntegrationMatch(match);
                                    route.goBack();
                                },
                            },
                        ]}
                    />
                </div>
                {t("integrations.syncing.reconnecting", tOptions)}
            </IonCardContent>
        </IonCard>
    );
};
